<script setup lang="ts" generic="V extends string | number = string | number">
import { computed, nextTick, ref } from 'vue'

import { useI18n } from '@/shared/i18n'
import { Icon, Select, type IconName, type SelectOption } from '@/shared/ui'

const model = defineModel<V | null>({ default: null })
const root = ref<HTMLElement | null>(null)
const props = withDefaults(
  defineProps<{
    options: SelectOption<V>[]
    label: string
    icon?: IconName
    placeholder?: string
    searchable?: boolean
    disabled?: boolean
    clearable?: boolean
    clearLabel?: string
    emptyLabel?: string
    wide?: boolean
    panelWidth?: string
  }>(),
  {
    placeholder: '',
    searchable: false,
    disabled: false,
    clearable: false,
    wide: false,
    panelWidth: 'min(18rem, calc(100vw - 0.75rem))',
  },
)
const { t } = useI18n()

// Both labels are translated fallbacks, never English defaults: an omitted `emptyLabel` is left
// unbound so `Select` resolves it from its own injected label bag instead of being overwritten.
const resolvedClearLabel = computed(() => props.clearLabel ?? t('ui.clear'))
const emptyLabelBinding = computed(() =>
  props.emptyLabel === undefined ? {} : { emptyLabel: props.emptyLabel },
)

const selectedLabel = computed(
  () => props.options.find((option) => option.value === model.value)?.label ?? props.placeholder,
)

async function clear(): Promise<void> {
  model.value = null
  await nextTick()
  root.value
    ?.querySelector<HTMLButtonElement>('.ui-select__picker-trigger')
    ?.focus({ preventScroll: true })
}
</script>

<template>
  <div
    ref="root"
    :style="{
      height: '2.75rem',
      minWidth: 0,
      width: wide ? '8.5rem' : '6.75rem',
    }"
    class="report-toolbar-select relative h-12 w-max max-w-39"
  >
    <Select
      v-model="model"
      :options="options"
      :placeholder="placeholder"
      :searchable="searchable"
      :disabled="disabled"
      v-bind="emptyLabelBinding"
      variant="picker"
      placement="bottom-end"
      :panel-width="panelWidth"
    >
      <template #trigger="{ open }">
        <span
          class="report-toolbar-select__trigger flex gap-1.5 w-full min-w-0 items-center"
          :aria-label="`${label}: ${selectedLabel}`"
        >
          <Icon v-if="icon" :name="icon" :size="16" aria-hidden="true" />
          <span
            class="report-toolbar-select__copy flex min-w-0 flex-1 flex-col items-start leading-none"
          >
            <strong>{{ label }}</strong>
            <small :title="selectedLabel">{{ selectedLabel }}</small>
          </span>
          <Icon
            v-if="!clearable || model === null"
            class="report-toolbar-select__chevron flex-none text-muted"
            :name="open ? 'chevron-up' : 'chevron-down'"
            :size="10"
            aria-hidden="true"
          />
        </span>
      </template>
    </Select>
    <button
      v-if="clearable && model !== null && !disabled"
      type="button"
      class="report-toolbar-select__clear absolute top-1/2 right-xs z-1 inline-flex h-5 items-center justify-center p-0 border-0 rounded-sm bg-transparent text-muted cursor-pointer transform-[translateY(-50%)] [&:hover]:bg-surface-sunken [&:hover]:text-primary"
      :aria-label="resolvedClearLabel"
      :title="resolvedClearLabel"
      @click.stop="clear"
    >
      <Icon name="x" :size="12" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.report-toolbar-select :deep(.ui-select__picker-trigger) {
  height: 2.75rem;
  gap: var(--ui-space-xs);
  padding: var(--ui-space-xs);
  border-radius: var(--ui-radius-md);
  color: var(--ui-color-primary-muted);
  cursor: pointer;
}
.report-toolbar-select :deep(.ui-select__picker-trigger:hover) {
  background-color: var(--ui-color-surface-sunken);
  color: var(--ui-color-primary);
}
.report-toolbar-select .report-toolbar-select__copy strong {
  max-width: 100%;
  overflow: hidden;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.report-toolbar-select .report-toolbar-select__copy small {
  max-width: 100%;
  overflow: hidden;
  color: var(--ui-color-muted);
  font-size: 0.625rem;
  font-weight: 400;
  line-height: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
